// The on-canvas "fit to view" / reset button — ported from domino's
// ViewportControls.ts (same shape, position, and corner-bracket icon), swapped
// onto mahjong's own render palette tokens. Anchored to the top-right of the
// pond ("tiles zone") the way domino anchors its button to the chain region,
// since the pond is this app's pannable/zoomable play surface.

import { roundedRect } from '@bn-games/render-helpers';
import type { Rect } from '../layout/types.js';
import {
  RESET_BUTTON_MARGIN_PX,
  RESET_BUTTON_RADIUS_PX,
  RESET_BUTTON_SIZE_PX,
} from '../utils/constants.js';
import type { RenderPalette } from './palette.js';

export interface ViewportControlsButton {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** The dark plate behind an on-canvas control; the pause gear is filled with it too. */
export const RESET_BUTTON_FILL = 'rgba(6, 32, 24, 0.82)';
const BORDER_COLOR = 'rgba(244, 230, 192, 0.35)';

/** Square fit-to-view button anchored to the top-right of the pond. */
export function layoutResetButton(pond: Rect): ViewportControlsButton {
  return {
    x: pond.x + pond.width - RESET_BUTTON_SIZE_PX - RESET_BUTTON_MARGIN_PX,
    y: pond.y + RESET_BUTTON_MARGIN_PX,
    w: RESET_BUTTON_SIZE_PX,
    h: RESET_BUTTON_SIZE_PX,
  };
}

/** `active` (the view has been panned/zoomed) swaps the fill to the claim-glow highlight. */
export function drawResetButton(
  ctx: CanvasRenderingContext2D,
  btn: ViewportControlsButton,
  palette: RenderPalette,
  active: boolean,
): void {
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  roundedRect(ctx, btn.x, btn.y, btn.w, btn.h, RESET_BUTTON_RADIUS_PX);
  ctx.fillStyle = active ? palette.claimGlow : RESET_BUTTON_FILL;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = BORDER_COLOR;
  ctx.lineWidth = 1.5;
  roundedRect(ctx, btn.x + 0.5, btn.y + 0.5, btn.w - 1, btn.h - 1, RESET_BUTTON_RADIUS_PX);
  ctx.stroke();

  // "Fit" icon: four corner brackets around the button's centre.
  ctx.strokeStyle = palette.compassText;
  ctx.lineWidth = 2;
  const cx = btn.x + btn.w / 2;
  const cy = btn.y + btn.h / 2;
  const r = 9;
  const arm = 4;
  ctx.beginPath();
  ctx.moveTo(cx - r, cy - r + arm);
  ctx.lineTo(cx - r, cy - r);
  ctx.lineTo(cx - r + arm, cy - r);
  ctx.moveTo(cx + r - arm, cy - r);
  ctx.lineTo(cx + r, cy - r);
  ctx.lineTo(cx + r, cy - r + arm);
  ctx.moveTo(cx + r, cy + r - arm);
  ctx.lineTo(cx + r, cy + r);
  ctx.lineTo(cx + r - arm, cy + r);
  ctx.moveTo(cx - r + arm, cy + r);
  ctx.lineTo(cx - r, cy + r);
  ctx.lineTo(cx - r, cy + r - arm);
  ctx.stroke();
  ctx.restore();
}

export function hitTestResetButton(btn: ViewportControlsButton, x: number, y: number): boolean {
  return x >= btn.x && x <= btn.x + btn.w && y >= btn.y && y <= btn.y + btn.h;
}
